import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {MessageService} from 'primeng/api';
import {CampaignService} from '../../service/api/campaign.service';
import {CloneCampaignRequest, CloneCampaignResponse} from '../../model/dto/api/campaign-clone.interface';
import {CampaignDto} from '../../model/dto/entity/campaign.dto';
import {CampaignModeEnum} from '../../enum/campaign-mode.enum';
import {CampaignTypeEnum} from '../../enum/campaign-type.enum';

/**
 * F-014: Campaign Cloning Dialog Component
 *
 * Provides a dialog interface for cloning an existing campaign with options to:
 * - Customize the clone's name and description
 * - Select which elements to clone (selection criteria, refresh schedule, assignment rules)
 *
 * Acceptance Criteria:
 * - Given an existing campaign, when user clones it, then a new draft campaign is created with copied configuration
 * - Given a campaign is cloned, when the clone is viewed, then selection criteria match the original
 * - Given a campaign is cloned, when the clone is activated, then it has its own independent member list
 * - Given a campaign with complex criteria is cloned, when criteria are modified in clone, then original is unchanged
 */
@Component({
    selector: 'ct-campaign-clone-dialog',
    templateUrl: './campaign-clone-dialog.component.html',
    styleUrls: ['./campaign-clone-dialog.component.scss'],
    standalone: false
})
export class CampaignCloneDialogComponent implements OnChanges {

    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    @Input() campaign: CampaignDto;

    @Output() onCloneSuccess = new EventEmitter<CloneCampaignResponse>();
    @Output() onCloneError = new EventEmitter<string>();
    @Output() closed = new EventEmitter<void>();

    form: CloneCampaignRequest = {
        name: '',
        description: '',
        cloneSelectionCriteria: true,
        cloneRefreshSchedule: true,
        cloneAssignmentRules: true
    };

    busy = false;

    constructor(
        protected campaignService: CampaignService,
        protected messageService: MessageService
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible && this.campaign) {
            this.resetForm();
        }
    }

    resetForm(): void {
        this.form = {
            name: '',
            description: this.campaign?.description || '',
            cloneSelectionCriteria: true,
            cloneRefreshSchedule: true,
            cloneAssignmentRules: true
        };
    }

    get isNameInvalid(): boolean {
        return this.form.name && this.form.name.trim().length > 255;
    }

    get isDescriptionInvalid(): boolean {
        return this.form.description && this.form.description.length > 2000;
    }

    get isFormValid(): boolean {
        return !this.isNameInvalid && !this.isDescriptionInvalid;
    }

    get namePlaceholder(): string {
        return this.campaign ? `${this.campaign.name} (Kopie)` : 'Kampagnenname (Kopie)';
    }

    get isRollingCampaign(): boolean {
        return this.campaign?.campaignMode === CampaignModeEnum.rolling;
    }

    get campaignTypeLabel(): string {
        if (!this.campaign) return '';
        const labels: { [key in CampaignTypeEnum]: string } = {
            [CampaignTypeEnum.customer]: 'Kunde',
            [CampaignTypeEnum.contract]: 'Vertrag'
        };
        return labels[this.campaign.campaignType] || String(this.campaign.campaignType);
    }

    get campaignModeLabel(): string {
        if (!this.campaign) return '';
        const labels: { [key in CampaignModeEnum]: string } = {
            [CampaignModeEnum.rolling]: 'Fortlaufend',
            [CampaignModeEnum.snapshot]: 'Momentaufnahme'
        };
        return labels[this.campaign.campaignMode] || String(this.campaign.campaignMode);
    }

    async submit(): Promise<void> {
        if (!this.isFormValid || !this.campaign) {
            return;
        }

        this.busy = true;

        const request: CloneCampaignRequest = {
            name: this.form.name?.trim() || undefined,
            description: this.form.description?.trim() || undefined,
            cloneSelectionCriteria: this.form.cloneSelectionCriteria,
            cloneRefreshSchedule: this.form.cloneRefreshSchedule,
            cloneAssignmentRules: this.form.cloneAssignmentRules
        };

        this.campaignService.cloneCampaign(this.campaign.id, request).subscribe({
            next: (response) => {
                this.busy = false;
                if (response.success) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Kampagne geklont',
                        detail: `„${response.clonedCampaignName}" wurde erfolgreich als Entwurfskampagne erstellt`
                    });
                    this.onCloneSuccess.emit(response);
                    this.closeDialog();
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Klonen fehlgeschlagen',
                        detail: response.message
                    });
                    this.onCloneError.emit(response.message);
                }
            },
            error: (error) => {
                this.busy = false;
                const errorMessage = error?.error?.message || 'Kampagne konnte nicht geklont werden';
                this.messageService.add({
                    severity: 'error',
                    summary: 'Klonen fehlgeschlagen',
                    detail: errorMessage
                });
                this.onCloneError.emit(errorMessage);
            }
        });
    }

    cancel(): void {
        this.closeDialog();
    }

    closeDialog(): void {
        this.visibleChange.emit(false);
        this.closed.emit();
    }
}
